import React from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from "react-native";
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";

import UserImageLoading from "./UserImageLoading";

import { type UserImageMedia } from "@/schemas/user/image";

interface ProfilePressProps {
  username: string;
  onBeforeProfilePress?: () => void;
}

interface ProfileFollowProps {
  isFollowing: boolean;
  isLoading: boolean;
  onPress: () => Promise<void>;
}

interface UserAvatarProps {
  avatar: UserImageMedia | null;
  defaultAvatarColor: string;
  size: number;
  profilePress?: ProfilePressProps;
  profileFollow?: ProfileFollowProps;
}

// TODO; Memoize this component for performance enhancements.
function UserAvatar({
  avatar,
  size,
  defaultAvatarColor,
  profilePress,
  profileFollow,
}: UserAvatarProps): React.JSX.Element {
  const router = useRouter();

  const handleProfilePress = async (username: string): Promise<void> => {
    profilePress?.onBeforeProfilePress?.();

    router.push({
      params: { username },
      pathname: "/(main)/shared/profile",
    });
  };

  const borderWidth = 0;
  const imageSize = size - 2 * borderWidth;

  const containerStyle: ViewStyle = {
    height: size,
    width: size,
  };

  const gradientColors = profileFollow?.isFollowing
    ? (["#4F46E5", "#7C3AED"] as const) // Indigo to Purple
    : (["#10B981", "#3B82F6"] as const); // Emerald to Blue]

  return (
    <View style={[styles.container, containerStyle]}>
      <TouchableOpacity
        disabled={!profilePress}
        {...(profilePress && {
          onPress: () => handleProfilePress(profilePress.username),
        })}
        style={styles.avatarTouchable}
        accessibilityLabel="Profile"
        accessibilityRole="button"
      >
        {avatar?.attributes.previewUrl ? (
          <UserImageLoading
            imageUrl={avatar.attributes.previewUrl}
            width={imageSize}
            height={imageSize}
            borderRadiusTop={imageSize / 2}
            borderRadiusBottom={imageSize / 2}
          />
        ) : (
          <View
            style={[
              styles.defaultAvatar,
              {
                borderRadius: imageSize / 2,
                height: imageSize,
                width: imageSize,
              },
            ]}
          >
            <MaterialCommunityIcons
              name="face-man-outline"
              size={imageSize}
              color={defaultAvatarColor}
            />
          </View>
        )}
      </TouchableOpacity>
      {profileFollow && (
        <TouchableOpacity
          style={[
            styles.followButton,
            {
              borderRadius: size * 0.175,
              height: size * 0.2,
              width: size * 0.2,
            },
          ]}
          onPress={profileFollow.onPress}
          disabled={profileFollow.isLoading}
        >
          <LinearGradient
            colors={gradientColors}
            style={[
              styles.followButtonGradient,
              { borderRadius: size * 0.175 },
            ]}
          >
            {profileFollow.isLoading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Ionicons
                name={profileFollow.isFollowing ? "remove" : "add"}
                size={size * 0.15}
                color="white"
              />
            )}
          </LinearGradient>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  avatarTouchable: {
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    position: "relative",
  },
  defaultAvatar: {
    alignItems: "center",
    justifyContent: "center",
  },
  followButton: {
    alignItems: "center",
    bottom: 10,
    elevation: 5,
    justifyContent: "center",
    position: "absolute",
    right: 10,
    shadowColor: "#000",
    shadowOffset: { height: 2, width: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  followButtonGradient: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    width: "100%",
  },
});

export default UserAvatar;
